import { useState } from 'react'
import { Alert, Button, Form, Input, Modal, Space, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { fetchMyCollectLimit, requestCollectCredit } from './collectLimitApi'

const LOW_SHARE = 0.1

/** Kassirning yig'ish limiti qoldig'i va qo'shimcha limit so'rash (ADR-054); limiti yo'q xodimga ko'rinmaydi. */
export function MyCollectLimit() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<{ amount: Money | null; note?: string }>()
  const [open, setOpen] = useState(false)
  const mine = useQuery({ queryKey: queryKeys.collectLimits.mine, queryFn: ({ signal }) => fetchMyCollectLimit(signal) })

  const request = useMutation({
    mutationFn: (values: { amount: Money | null; note?: string }) =>
      requestCollectCredit(values.amount?.amount ?? 0, values.note?.trim() || null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collectLimits.all })
      setOpen(false)
    },
  })

  const close = () => {
    request.reset()
    setOpen(false)
  }

  const data = mine.data
  if (!data || (!data.limited && !data.pending)) return null
  const money = (amount: number) => formatMoneyValue({ amount, currency: data.currency })
  const low = data.limited && data.remaining <= data.limit * LOW_SHARE

  return (
    <>
      <Alert
        style={{ marginBottom: 16 }}
        type={!data.limited ? 'info' : data.remaining <= 0 ? 'error' : low ? 'warning' : 'info'}
        showIcon
        message={
          data.limited
            ? t('collectLimit.mine.remaining', { remaining: money(Math.max(0, data.remaining)), limit: money(data.limit) })
            : t('collectLimit.mine.noLimit')
        }
        description={
          <Space direction="vertical" size={4}>
            {data.limited ? (
              <Typography.Text type="secondary">{t('collectLimit.mine.used', { used: money(data.used) })}</Typography.Text>
            ) : null}
            {data.pending ? (
              <Typography.Text>{t('collectLimit.mine.pending', { amount: money(data.pending.amount) })}</Typography.Text>
            ) : null}
          </Space>
        }
        action={
          data.pending ? null : (
            <Button size="small" onClick={() => setOpen(true)}>
              {t('collectLimit.mine.request')}
            </Button>
          )
        }
      />
      <Modal
        open={open}
        title={t('collectLimit.mine.requestTitle')}
        okText={t('collectLimit.mine.send')}
        cancelText={t('common.cancel')}
        confirmLoading={request.isPending}
        onCancel={close}
        onOk={() => submitOnValid(form, (values) => request.mutate(values))}
        destroyOnHidden
      >
        {request.isError ? (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message={translateError(request.error, t)} />
        ) : null}
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="amount"
            label={t('collectLimit.amount')}
            rules={[{ required: true, message: t('collectLimit.amountRequired') }]}
          >
            <MoneyInput currency={data.currency} minMinor={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label={t('collectLimit.note')}>
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
